/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';

import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Alert, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import QueryString from 'qs';
import { useEffect, useMemo } from 'react';
import FormProvider from '../hook-form/form-provider';
import RHFTextField from '../hook-form/rhf-text-field';
import { encryptRequest, decryptResponse } from '@/app/utilities/helpers/security';
import { axiosInstance } from '@/app/utilities/helpers/axios';
import { MAXIMUM_TRANSFER_COUNT } from '@/app/utilities/consts';

export default function DlgContent({ macAddress, dialog, transferCount }) {
  const { enqueueSnackbar } = useSnackbar();

  const Schema = Yup.object().shape({
    current: Yup.string()
      .required('Current Mac-address is required')
      .matches(/^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/, 'Invalid MAC address format'),
    new: Yup.string()
      .required('New Mac-address is required')
      .matches(/^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/, 'Invalid MAC address format'),
  });

  const defaultValues = useMemo(
    () => ({
      current: macAddress,
      new: '',
    }),
    [macAddress]
  );

  const methods = useForm({
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = methods;

  const onSubmit = handleSubmit(async data => {
    try {
      const body = {
        data: encryptRequest({
          current: macAddress,
          new_: data.new,
        }),
      };

      const response = await axiosInstance.post(
        'PwkgU8y3VCTyZxpyVerTRktXmyscYqFA',
        QueryString.stringify(body)
      );

      const responseData = decryptResponse(response.data);

      if (responseData.success) {
        enqueueSnackbar(responseData.message, { variant: 'success' });
        reset(defaultValues);
        window.location.reload();
      } else {
        enqueueSnackbar(responseData.message, { variant: 'error' });
      }
      dialog.onFalse();
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
      dialog.onFalse();
    }
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const disabled = MAXIMUM_TRANSFER_COUNT - transferCount <= 0;

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <DialogTitle sx={{ pb: 2 }}>Transfer current activation to another device</DialogTitle>
      <DialogContent>
        <Stack gap={3} marginTop={2}>
          <Alert severity="info" variant="outlined">
            When this operation is performed, the mac-addresses of the devices are swapped.
            Therefore, the current mac-address remains active. Do not forget to restart the TV after
            performing the operation.
          </Alert>
          <RHFTextField name="current" label="Current Mac-address *" type="mac-address" readOnly />
          <RHFTextField name="new" label="New Mac-address" type="mac-address" />
          {disabled ? (
            <Alert severity="error" variant="outlined">
              You can not do this operation because you already tried 3 times. Please contact
              support for more
            </Alert>
          ) : (
            <Alert severity="warning" variant="outlined">
              {`This operation is possible up to 3 times and cannot be done more than that. The current
            possible number of attempts is ${MAXIMUM_TRANSFER_COUNT - transferCount}`}
            </Alert>
          )}

          <Stack gap={3} flexDirection="row" justifyContent="flex-end" marginBottom={2}>
            <Button onClick={dialog.onFalse}>Cancel</Button>
            <LoadingButton
              variant="contained"
              type="submit"
              loading={isSubmitting}
              disabled={disabled}
            >
              Submit
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogContent>
    </FormProvider>
  );
}
